/* eslint-disable no-console */
import { CertificationStatusChipOrIcon, EmbeddableVirtualConnectionDataQualityWarning } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { SiteUrlsProps } from '~src/app/selectors/SiteUrlsSelectors';
import { TagsMetadata } from '~src/app/spaces/Header/TagsMetadata';
import { CatalogApiRequests } from '~src/catalog/CatalogApiRequests';
import { catalogApolloClient } from '~src/catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '~src/catalog/CatalogFeatureFlagsAdapter';
import { CERTIFIED_INDICATOR_TYPE, dataQualityIndicatorInfosToProps } from '~src/catalog/components/dataQuality/DataQualityType';
import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/catalog/models/IDataQualityIndicatorInfo';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { DataCatalogUIActionsService } from '~src/catalog/services/DataCatalogUIActionsService';
import { sanitize } from '~src/commonInfra/utils/Sanitize';
import {
  EditPublishedConnectionEntityAction,
} from '~src/publishedConnection/entityActions/EditPublishedConnectionEntityAction';
import { IPublishedConnectionActions } from '~src/publishedConnection/serverApi/types/IPublishedConnectionActions';
import { isPublishedConnectionTableInfo } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { ItemDescription } from '~src/spacesPlatform/components/header/HeaderItemDescription';
import { ExtractStatusMetadata, ModifiedDateMetadata, OwnerMetadata, PublishedConnectionPolicyMetaData } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, FavoriteToggleWithCountsIcon } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader, SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ISpaceItemDetailInfo } from '~src/ts/models/ISpaceItemDetailInfo';

import { PublishedConnections } from '../resources/PublishedConnections';

export interface PublishedConnectionSpaceHeaderProps extends SiteUrlsProps {
  readonly itemInfo: ISpaceItemDetailInfo<'virtualConnection'>;
  readonly availableActions: IPublishedConnectionActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}

export function PublishedConnectionSpaceHeader(props: PublishedConnectionSpaceHeaderProps): JSX.Element {
  const tagUrl = React.useCallback((tag: string) => props.urls.publishedConnectionsWithTagUrl(tag), [props.urls]);

  return (
    <ConnectedSpaceHeader
      item={props.itemInfo}
      availableActions={{...props.availableActions}}
      nameRowIcons={
        <React.Fragment>
          {FavoriteToggleWithCountsIcon({
            item: props.itemInfo,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          {ActionsMenuIcon(props.itemInfo)}
        </React.Fragment>
      }
      metadataRow={
        <React.Fragment>
          {OwnerMetadata(props.itemInfo.owner, props.urls)}
          {ModifiedDateMetadata(props.itemInfo.updatedAt)}
          {ExtractStatusMetadata(props.itemInfo.hasExtracts)}
          {isPublishedConnectionTableInfo(props.itemInfo) && PublishedConnectionPolicyMetaData(props.itemInfo)}
          {CertificationMetadata(props.itemInfo, props.urls)}
          {selectCatalogingOnSiteEnabled &&
            <DataQualityMetadata itemInfo={props.itemInfo} urls={props.urls} />}
        </React.Fragment>
      }
      spotlightActions={[EditPublishedConnectionEntityAction]}
      descriptionElement={ItemDescription(props.itemInfo, props.openDetail, sanitize)}
      bottomElement={TagsMetadata({ itemInfo: props.itemInfo, tagUrl })}
    />
  );
}

function DataQualityMetadata(props: {
  itemInfo: ISpaceItemDetailInfo<'virtualConnection'>;
  urls: SiteUrls;
}): JSX.Element | null {
  const anchoredWarnings = props.itemInfo.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE);
  const dataQualityWarningsOnThisItem = dataQualityIndicatorInfosToProps(
    {
      assetUrl: props.urls.publishedConnectionUrl(props.itemInfo.id),
      assetName: props.itemInfo.name,
      assetType: 'VirtualConnection',
      onContentItem: true
    },
    props.itemInfo.dataQualityIndicators
  );

  return (
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      <EmbeddableVirtualConnectionDataQualityWarning
        client={catalogApolloClient}
        vizportalUrlId={props.itemInfo.id}
        ownContentWarnings={dataQualityWarningsOnThisItem}
        siteUrls={props.urls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        featureFlags={catalogFeatureFlagsAdapter()}
        asset={{
          assetName: props.itemInfo.name,
          assetType: 'VirtualConnection',
          assetUrl: urls => urls.publishedConnectionUrl(props.itemInfo.id)
        }}
        anchoredWarnings={anchoredWarnings.map(i => dataQualityIndicatorInfoToDataQualityIndicator(i))}
      />
    </span>
  );
}

function CertificationMetadata(
  itemInfo: ISpaceItemDetailInfo<'virtualConnection'>,
  urls: SpacesSiteUrls
): JSX.Element {
  const isCertified = PublishedConnections.hasCertification(itemInfo);
  const indicators = itemInfo.dataQualityIndicators.filter(i => i.type === CERTIFIED_INDICATOR_TYPE);
  const indicator = indicators[0];

  return isCertified &&
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin} data-tb-test-id={SpaceHeaderTestIds.certifiedBy}>
      <CertificationStatusChipOrIcon
        subjectArea={'VirtualConnection'}
        kind={'vizportal'}
        anchoredAsset={{
          assetName: itemInfo.name,
          assetType: 'VirtualConnection',
          assetUrl: urls => urls.publishedConnectionUrl(itemInfo.id)
        }}
        anchoredCertifications={[dataQualityIndicatorInfoToDataQualityIndicator(indicator)]}
        iconOnly={false}
        urls={urls}
      />
    </span>;
}
